import { apiClient } from '@/services/_shared/apiClient';
import ENDPOINTS from '@/utils/endpoints';
import { useAuthStore } from '@/stores/authStore';

// ─── Types ──────────────────────────────────────────────────

export interface NotificationPreferencesAPI {
  email_enabled: boolean;
  push_enabled: boolean;
  websocket_enabled: boolean;
  frequency: 'realtime' | 'hourly' | 'daily' | 'weekly';
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  timezone: string;
  categories: string[];
  email_categories: string[];
  push_categories: string[];
  disabled_types: string[];
  digest_mode: boolean;
  sound_enabled: boolean;
  desktop_enabled: boolean;
}

export type NotificationCategory =
  | 'order'
  | 'payment'
  | 'ticket'
  | 'task'
  | 'system'
  | 'site'
  | 'dispute';
export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface NotificationItem {
  id: string;
  user_id: string;
  app_source: string;
  type: string;
  title: string;
  message: string;
  data: {
    action_url?: string;
    order_id?: string;
    order_number?: string;
    site_domain?: string;
    transaction_id?: string;
    ticket_id?: string;
    dispute_id?: string;
    amount?: string;
    [key: string]: unknown;
  };
  read_at: string | null;
  sent_via: string[];
  priority: NotificationPriority;
  category: NotificationCategory;
  related_entity_id: string | null;
  related_entity_type: string | null;
  scheduled_for: string | null;
  sent_at: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationListParams {
  per_page?: number;
  page?: number;
  read?: 'true' | 'false';
  type?: string;
  category?: NotificationCategory;
  priority?: NotificationPriority;
  from_date?: string;
  to_date?: string;
}

export interface NotificationListResponse {
  notifications: NotificationItem[];
  meta: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
}

export interface UnreadCountResponse {
  total: number;
  by_category: Record<string, number>;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface ApiListResponse {
  success: boolean;
  data: NotificationItem[];
  meta: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
  message?: string;
}

// ─── Helpers ────────────────────────────────────────────────

function getUserId(): string {
  const user = useAuthStore.getState().user;
  if (!user?.id) throw new Error('User not authenticated');
  return user.id;
}

// ─── Preferences API ────────────────────────────────────────

function userIdHeader() {
  return { 'X-User-Id': getUserId() };
}

export async function getNotificationPreferences(): Promise<NotificationPreferencesAPI> {
  const response = await apiClient.get<ApiResponse<NotificationPreferencesAPI>>(
    ENDPOINTS.NOTIFICATION_PREFERENCES,
    { headers: userIdHeader() }
  );
  return response.data.data;
}

export async function updateNotificationPreferences(
  updates: Partial<NotificationPreferencesAPI>
): Promise<NotificationPreferencesAPI> {
  const response = await apiClient.put<ApiResponse<NotificationPreferencesAPI>>(
    ENDPOINTS.NOTIFICATION_PREFERENCES,
    updates,
    { headers: userIdHeader() }
  );
  return response.data.data;
}

export async function resetNotificationPreferences(): Promise<NotificationPreferencesAPI> {
  const response = await apiClient.post<ApiResponse<NotificationPreferencesAPI>>(
    ENDPOINTS.NOTIFICATION_PREFERENCES_RESET,
    {},
    { headers: userIdHeader() }
  );
  return response.data.data;
}

// ─── Notifications API ──────────────────────────────────────

export async function getNotifications(
  params: NotificationListParams = {}
): Promise<NotificationListResponse> {
  const response = await apiClient.get<ApiListResponse>(ENDPOINTS.NOTIFICATIONS, { params });
  return {
    notifications: response.data.data ?? [],
    meta: response.data.meta ?? { current_page: 1, per_page: 15, total: 0, last_page: 1 },
  };
}

export async function getUnreadCount(): Promise<UnreadCountResponse> {
  const response = await apiClient.get<ApiResponse<UnreadCountResponse>>(
    ENDPOINTS.NOTIFICATIONS_UNREAD_COUNT
  );
  return response.data.data;
}

export async function markAsRead(id: string): Promise<void> {
  await apiClient.put(ENDPOINTS.NOTIFICATION_READ(id), {});
}

export async function markAllAsRead(): Promise<void> {
  await apiClient.put(ENDPOINTS.NOTIFICATIONS_READ_ALL, {});
}

export async function deleteNotification(id: string): Promise<void> {
  await apiClient.delete(ENDPOINTS.NOTIFICATION_DELETE(id));
}

export async function deleteAllRead(): Promise<void> {
  await apiClient.delete(ENDPOINTS.NOTIFICATIONS_DELETE_READ);
}
